<template>
  <section class="form-fw__content">
    <form
      v-if="status === isSuccess"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <FormBox
        :loading="status === isLoading"
        :title="routeName === 'CreateCompany' ? $t(`${namespace}.financialConfig`) : $t(`${namespace}.general`)"
        :icon="require('@/assets/icons/icn_money-paper-accent.svg')"
      >
        <v-layout row wrap>
          <v-flex sm6 xs12>
            <div class="pr-2--desktop">
              <label>{{ $t(`${namespace}.fieldPeriodicityConfigTypeLabel`) }}</label>
              <v-select
                v-model="form.periodicityConfigType"
                :placeholder="$t(`${namespace}.fieldPeriodicityConfigTypePlaceholder`)"
                item-text="label"
                :items="fieldPeriodicityConfigTypeOptions"
                :error-messages="errors.collect($t(`${namespace}.fieldPeriodicityConfigTypeName`))"
                v-validate="{ required: true }"
                :data-vv-name="$t(`${namespace}.fieldPeriodicityConfigTypeName`)"
                noDataText=""
                required
                flat
                outline
                solo
                color="accent"
              />
            </div>
          </v-flex>

          <v-flex sm6 xs12>
            <div class="pl-2--desktop">
              <div
                v-if="form.periodicityConfigType === 'MONTHLY'"
                v-html="$t(`${namespace}.monthlyPeriodDisclaimer`)"
                class="period__disclaimer"
              />

              <div
                v-if="form.periodicityConfigType === 'MANY_DAYS'"
                v-html="$t(`${namespace}.biweeklyPeriodDisclaimer`)"
                class="period__disclaimer"
              />

              <div
                v-if="form.periodicityConfigType === 'DAILY'"
                v-html="$t(`${namespace}.dailyPeriodDisclaimer`)"
                class="period__disclaimer"
              />
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex sm6 xs12>
            <div class="pr-2--desktop">
              <label>{{ $t(`${namespace}.fieldPaymentMethodLabel`) }}</label>
              <div class="input__tooltip">
                <v-select
                  v-model="form.paymentMethod"
                  :placeholder="$t(`${namespace}.fieldPaymentMethodPlaceholder`)"
                  item-text="label"
                  :items="fieldPaymentMethodOptions"
                  :disabledItems="['CREDIT_CARD']"
                  :error-messages="errors.collect($t(`${namespace}.fieldPaymentMethodName`))"
                  v-validate="{ required: true }"
                  :data-vv-name="$t(`${namespace}.fieldPaymentMethodName`)"
                  noDataText=""
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />

                <v-tooltip min-width="204" max-width="204" top>
                  <v-icon color="accent" slot="activator">info</v-icon>
                  <span>{{ $t(`${namespace}.paymentMethodTooltip`) }}</span>
                </v-tooltip>
              </div>
            </div>
          </v-flex>

          <v-flex sm6 xs12>
            <div class="pl-2--desktop">
              <label>{{ $t(`${namespace}.fieldDueDaysLabel`) }}</label>
              <v-text-field
                v-model="form.dueDays"
                type="number"
                :placeholder="$t(`${namespace}.fieldDueDaysPlaceholder`)"
                :error-messages="errors.collect($t(`${namespace}.fieldDueDaysName`))"
                v-validate="{ required: true, integer: true }"
                :data-vv-name="$t(`${namespace}.fieldDueDaysName`)"
                required
                flat
                outline
                solo
                color="accent"
              />
            </div>
          </v-flex>
        </v-layout>

        <label>
          {{ $t(`${namespace}.fieldEmailsToNotifyLabel`) }}
          <small>({{ $t('Common.emailComboboxHint') }})</small>
        </label>
        <v-combobox
          v-model="form.emailsToNotify"
          :placeholder="$t(`${namespace}.fieldEmailsToNotifyPlaceholder`)"
          :error-messages="errors.collect($t(`${namespace}.fieldEmailsToNotifyName`))"
          v-validate="{ required: true, emailList: true }"
          :data-vv-name="$t(`${namespace}.fieldEmailsToNotifyName`)"
          noDataText=""
          multiple
          chips
          required
          flat
          outline
          solo
          color="accent"
        >
          <template v-slot:selection="{ item, parent, selected }">
            <v-chip
              :selected="selected"
              :class="[
                { 'error': !emailRegex.test(item) },
                { 'theme--dark': !emailRegex.test(item) },
              ]"
              close
              @input="parent.selectItem(item)"
            >
              {{ item }}
            </v-chip>
          </template>
        </v-combobox>

        <div v-if="false">
          <label>{{ $t(`${namespace}.fieldUserMessageLabel`) }}</label>
          <div class="textarea__restore">
            <v-textarea
              v-model="form.userMessage"
              :placeholder="$t(`${namespace}.fieldUserMessagePlaceholder`)"
              :error-messages="errors.collect($t(`${namespace}.fieldUserMessageName`))"
              v-validate="{ required: true, max: 200 }"
              :data-vv-name="$t(`${namespace}.fieldUserMessageName`)"
              required
              flat
              outline
              solo
              counter="200"
              color="accent"
            />

            <v-btn
              v-if="form.userMessage && form.userMessage !== userMessageBackup"
              flat
              icon
              @click="restoreMessage('userMessage')"
            >
              <v-icon color="accent">restore</v-icon>
            </v-btn>
          </div>

          <label>{{ $t(`${namespace}.fieldEmployeeMessageLabel`) }}</label>
          <div class="textarea__restore">
            <v-textarea
              v-model="form.employeeMessage"
              :placeholder="$t(`${namespace}.fieldEmployeeMessagePlaceholder`)"
              :error-messages="errors.collect($t(`${namespace}.fieldEmployeeMessageName`))"
              v-validate="{ required: true, max: 200 }"
              :data-vv-name="$t(`${namespace}.fieldEmployeeMessageName`)"
              required
              flat
              outline
              solo
              counter="200"
              color="accent"
            />

            <v-btn
              v-if="form.employeeMessage && form.employeeMessage !== employeeMessageBackup"
              flat
              icon
              @click="restoreMessage('employeeMessage')"
            >
              <v-icon color="accent">restore</v-icon>
            </v-btn>
          </div>
        </div>
      </FormBox>

      <FormBox
        :loading="status === isLoading"
        :title="$t(`${namespace}.saas`)"
        :icon="require('@/assets/icons/icn_paper-list-accent.svg')"
        :subheaderTitle="$t(`${namespace}.saasSubtitle`)"
        :subheaderDesc="$t(`${namespace}.saasDesc`)"
        toggle
        :visible="saasContainer"
        @toggleVisibility="(val) => resetContainerFields('saas', val)"
      >
        <v-layout row wrap>
          <v-flex sm6 xs12>
            <div class="pr-2--desktop">
              <label>{{ $t(`${namespace}.fieldStartDateLabel`) }}</label>
              <v-text-field
                v-model="serviceStartChargeDateFormatted"
                ref="input"
                mask="##/####"
                return-masked-value
                append-icon="today"
                :placeholder="$t(`${namespace}.fieldStartDatePlaceholder`)"
                :error-messages="errors.collect($t(`${namespace}.fieldStartDateName`))"
                v-validate="{
                  required: true,
                  date_format: 'MM/yyyy',
                  isValidDate: true
                }"
                :data-vv-name="$t(`${namespace}.fieldStartDateName`)"
                required
                flat
                outline
                solo
                color="accent"
                @click:append="openDialog"
              />

              <v-dialog
                v-model="dialog"
                :open-on-click="false"
                :close-on-content-click="false"
                lazy
                full-width
                width="290"
              >
                <v-date-picker
                  v-model="date"
                  :locale="currentLocale"
                  :min="moment(today).add(1, 'months').format('YYYY-MM-DD')"
                  color="accent"
                  type="month"
                  @change="closeDialog"
                />
              </v-dialog>
            </div>
          </v-flex>

          <v-flex sm6 xs12>
            <div class="pl-2--desktop">
              <div
                v-html="$t(`${namespace}.sassDateDisclaimer`)"
                class="period__disclaimer"
              />
            </div>
          </v-flex>
        </v-layout>

        <div>
          <v-checkbox
            v-model="serviceMonthlyFeeCheckbox"
            :label="$t(`${namespace}.fieldMonthlyFeeLabel`)"
            class="mb-2 mt-0"
            hide-details
          />

          <div v-if="serviceMonthlyFeeCheckbox" class="input--padding-left">
            <v-text-field
              v-model="serviceMonthlyFeeFormatted"
              ref="serviceMonthlyFeeFormatted"
              v-money="money"
              :placeholder="$t(`${namespace}.fieldMonthlyFeePlaceholder`)"
              :error-messages="errors.collect($t(`${namespace}.fieldMonthlyFeeName`))"
              v-validate="{ required: true, isValidValue: true }"
              :data-vv-name="$t(`${namespace}.fieldMonthlyFeeName`)"
              required
              flat
              outline
              solo
              color="accent"
            />
          </div>
        </div>

        <div>
          <v-checkbox
            v-model="serviceTaxiJobFeeCheckbox"
            :label="$t(`${namespace}.fieldRideTaxLabel`)"
            class="mb-2 mt-0"
            hide-details
          />

          <div v-if="serviceTaxiJobFeeCheckbox" class="input--padding-left">
            <v-text-field
              v-model="serviceTaxiJobFeeFormatted"
              ref="serviceTaxiJobFeeFormatted"
              v-money="money"
              :placeholder="$t(`${namespace}.fieldRideTaxPlaceholder`)"
              :error-messages="errors.collect($t(`${namespace}.fieldRideTaxName`))"
              v-validate="{ required: true, isValidValue: true }"
              :data-vv-name="$t(`${namespace}.fieldRideTaxName`)"
              required
              flat
              outline
              solo
              color="accent"
            />
          </div>
        </div>
      </FormBox>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="tryAgain"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>
  </section>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { VMoney } from 'v-money'
import { isObject, isArray, convertToCoin, stringToFloat, objectToArray, goBack } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import FormBox from 'components/FormBox'

export default {
  name: 'CostCenterForm',
  props: {
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  components: {
    FormBox
  },
  directives: {
    money: VMoney
  },
  computed: {
    isLoading: () => STATUS_IS_LOADING,

    isSuccess: () => STATUS_SUCCESS,

    isFailed: () => STATUS_FAILED,

    routeName () {
      return this.$route.name
    },

    companyId () {
      return this.$route.params && this.$route.params.id
        ? this.$route.params.id
        : null
    },

    today () {
      return new Date().toJSON().slice(0, 10)
    },

    currentLocale () {
      return this.$i18n.locale.split('_').join('-')
    },

    fieldPaymentMethodOptions () {
      const options = objectToArray(this.$t(`${this.namespace}.fieldPaymentMethodOptions`))
      return this.form.periodicityConfigType === 'DAILY'
        ? options.filter(opt => opt.value === 'CREDIT_CARD')
        : options
    },

    fieldPeriodicityConfigTypeOptions () {
      const paymentMethod = this.form.paymentMethod

      return paymentMethod === 'CREDIT_CARD'
        ? objectToArray(this.$t(`${this.namespace}.fieldPeriodicityConfigTypeOptions`))
        : objectToArray(this.$t(`${this.namespace}.fieldPeriodicityConfigTypeOptions`)).map(item => {
          if (item.value === 'DAILY') item.disabled = true
          return item
        })
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyFinancialConfigPage',
      isFetching: false,
      emailRegex: /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/,
      userMessageBackup: 'user message backup',
      employeeMessageBackup: 'employee message backup',

      // containers visibility
      saasContainer: false,

      // mask money config
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },

      // datepicker
      date: null,
      dialog: false,

      // form fields
      serviceMonthlyFeeCheckbox: false,
      serviceTaxiJobFeeCheckbox: false,
      serviceStartChargeDateFormatted: null,
      serviceMonthlyFeeFormatted: null,
      serviceTaxiJobFeeFormatted: null,
      form: {}
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    tryAgain () {
      if (this.routeName === 'CompanyFinancialConfig') {
        this.$emit('tryAgain')
        return
      }

      location.reload()
    },

    isValidObject: (val) => isObject(val),

    isValidArray: (val) => isArray(val),

    objectToArray: (val) => objectToArray(val),

    prevPage () {
      goBack()
    },

    renderPayload () {
      if (this.isValidObject(this.data)) {
        const {
          periodicityConfigType,
          paymentMethod,
          dueDays,
          emailsToNotify,
          isSaasActive,
          serviceStartChargeDate,
          serviceMonthlyFee,
          serviceTaxiJobFee
        } = this.data

        this.form = {
          periodicityConfigType,
          paymentMethod,
          dueDays,
          emailsToNotify,
          isSaasActive,
          serviceStartChargeDate,
          serviceMonthlyFee,
          serviceTaxiJobFee
        }

        // ==============================================
        // saas container
        // ==============================================

        this.saasContainer = isSaasActive

        // checkbox
        setTimeout(() => {
          this.serviceMonthlyFeeCheckbox = Boolean(serviceMonthlyFee)
          this.serviceTaxiJobFeeCheckbox = Boolean(serviceTaxiJobFee)
        }, 500)

        setTimeout(() => {
          if (this.$refs['serviceMonthlyFeeFormatted'] && this.$refs['serviceMonthlyFeeFormatted'].$el.querySelector('input')) {
            this.$refs['serviceMonthlyFeeFormatted'].$el.querySelector('input').value = convertToCoin(this.data.serviceMonthlyFee)
            this.serviceMonthlyFeeFormatted = convertToCoin(this.data.serviceMonthlyFee)
          }

          if (this.$refs['serviceTaxiJobFeeFormatted'] && this.$refs['serviceTaxiJobFeeFormatted'].$el.querySelector('input')) {
            this.$refs['serviceTaxiJobFeeFormatted'].$el.querySelector('input').value = convertToCoin(this.data.serviceTaxiJobFee)
            this.serviceTaxiJobFeeFormatted = convertToCoin(this.data.serviceTaxiJobFee)
          }

          this.form.serviceStartChargeDate = serviceStartChargeDate
          this.serviceStartChargeDateFormatted = moment(serviceStartChargeDate).format('MM/YYYY')
        }, 1000)
      }
    },

    restoreMessage (field) {
      if (field === 'userMessage') this.form.userMessage = this.userMessageBackup
      if (field === 'employeeMessage') this.form.employeeMessage = this.employeeMessageBackup
    },

    resetMoneyFields (fields = 'all') {
      if (fields === 'all' || fields === 'serviceMonthlyFee') {
        this.form.serviceMonthlyFee = null
        this.serviceMonthlyFeeFormatted = null

        if (this.$refs['serviceMonthlyFeeFormatted']) {
          this.$refs['serviceMonthlyFeeFormatted'].$el.querySelector('input').value = 'R$ 0,00'
        }
      }

      if (fields === 'all' || fields === 'serviceTaxiJobFee') {
        this.form.serviceTaxiJobFee = null
        this.serviceTaxiJobFeeFormatted = null

        if (this.$refs['serviceTaxiJobFeeFormatted']) {
          this.$refs['serviceTaxiJobFeeFormatted'].$el.querySelector('input').value = 'R$ 0,00'
        }
      }
    },

    resetSassFields () {
      this.serviceMonthlyFeeCheckbox = false
      this.serviceTaxiJobFeeCheckbox = false
      this.form.isSaasActive = false
      this.form.serviceStartChargeDate = null
      this.serviceStartChargeDateFormatted = null
      this.resetMoneyFields()
    },

    resetAllFields () {
      this.form.periodicityConfigType = null
      this.form.paymentMethod = null
      this.form.dueDays = null
      this.form.emailsToNotify = []
    },

    resetContainerFields (container, val) {
      if (container === 'saas') {
        this.resetSassFields()
        this.form.isSaasActive = val
      }
    },

    openDialog () {
      this.dialog = true
    },

    closeDialog () {
      this.dialog = false

      const date = this.date
      const [y, m] = date.split('-')
      const newDate = [].concat(y, m).reverse().join('/')
      this.serviceStartChargeDateFormatted = newDate
      this.form.serviceStartChargeDate = `${this.date}-01T00:00:00.000-03:00`

      this.$refs.input.focus()
      setTimeout(() => this.$refs.input.blur(), 200)
    },

    async getCompany () {
      try {
        const companyId = this.companyId
        const { data: company } = await this.$apiService.company.getById(companyId)

        if (!company || !this.isValidObject(company)) {
          throw new Error()
        }
        const { name: companyName, documentId } = company.data

        this.form.companyId = companyId
        this.form.companyName = companyName
        this.form.documentId = documentId
      } catch (err) {
        console.log('ERROR:', err)
        this.$emit('setCurrentStatus', this.isFailed)
      }
    },

    async create () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const serviceMonthlyFee = typeof this.form.serviceMonthlyFee === 'number' && this.form.serviceMonthlyFee > 0
          ? this.form.serviceMonthlyFee
          : null

        const serviceTaxiJobFee = typeof this.form.serviceTaxiJobFee === 'number' && this.form.serviceTaxiJobFee > 0
          ? this.form.serviceTaxiJobFee
          : null

        await this.getCompany()

        const { data: res } = await this.$apiService.company.createFinancialConfig({
          ...this.form,
          serviceMonthlyFee,
          serviceTaxiJobFee
        })

        if (res && !res.valid) {
          const msg = this.isValidArray(res.reasons) ? res.reasons[0] : null
          this.showAlert({
            type: 'error',
            message: msg
          })
        } else {
          this.$router.push({ name: 'Company' }).catch(() => {})
          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.createSuccessMessage`)
          })
        }

        scrollTo(0, 0)
        this.$emit('setCurrentStatus', this.isSuccess)
        // this.$emit('tryAgain', this.isSuccess)
      } catch (err) {
        console.log('ERROR', err)
        this.$emit('setCurrentStatus', this.isSuccess)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.createErrorMessage`)
        })
      }
    },

    async edit () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const serviceMonthlyFee = typeof this.form.serviceMonthlyFee === 'number' && this.form.serviceMonthlyFee > 0
          ? this.form.serviceMonthlyFee
          : null

        const serviceTaxiJobFee = typeof this.form.serviceTaxiJobFee === 'number' && this.form.serviceTaxiJobFee > 0
          ? this.form.serviceTaxiJobFee
          : null

        const { data: res } = await this.$apiService.setWalletFinancialConfig(this.companyId, {
          ...this.form,
          serviceMonthlyFee,
          serviceTaxiJobFee
        })

        if (res && !res.valid) {
          const msg = this.isValidArray(res.reasons) ? res.reasons[0] : null
          this.showAlert({
            type: 'error',
            message: msg
          })
        } else {
          this.$router.push({ name: 'Company' }).catch(() => {})
          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.editSuccessMessage`)
          })
        }

        scrollTo(0, 0)
        this.$emit('setCurrentStatus', this.isSuccess)
        // this.$emit('tryAgain', this.isSuccess)
      } catch (err) {
        console.log('ERROR', err)
        this.$emit('setCurrentStatus', this.isSuccess)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.editErrorMessage`)
        })
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        // TO DO
        // this.dispatchHeaderErrorMessage()

        if (isValid) {
          this.routeName === 'CompanyFinancialConfig'
            ? this.edit()
            : this.create()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            elem.scrollIntoView(false)
            elem.focus()
          }
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    }
  },
  watch: {
    submit () {
      this.handleSubmit()
    },

    data () {
      this.renderPayload()
    },

    saasContainer (val) {
      this.form.isSaasActive = val
      if (!val) this.resetSassFields()
    },

    serviceMonthlyFeeFormatted (val) {
      this.form.serviceMonthlyFee = stringToFloat(val)
    },

    serviceTaxiJobFeeFormatted (val) {
      this.form.serviceTaxiJobFee = stringToFloat(val)
    },

    serviceMonthlyFeeCheckbox (val) {
      this.resetMoneyFields('serviceMonthlyFee')
    },

    serviceTaxiJobFeeCheckbox (val) {
      this.resetMoneyFields('serviceTaxiJobFee')
    }
  },
  created () {
    this.$validator.extend('emailList', {
      getMessage: field => this.$t(`${this.namespace}.emailErrorMessage`),
      validate: value => {
        const validate = value.find(email => !this.emailRegex.test(email))
        return !validate
      }
    })

    this.$validator.extend('isValidValue', {
      getMessage: field => this.$t('Common.pleaseEnterValidValue'),
      validate: value => !/(R\$\s0,00)$/.test(value)
    })

    this.$validator.extend('isValidDate', {
      getMessage: field => this.$t(`${this.namespace}.startDateErrorMessage`),
      validate: value => {
        const today = `${moment().format('YYYY-MM')}-01T00:00:00`
        const todayTimestamp = new Date(today).getTime()

        const dateFormat = value.split('/').reverse().join('-')
        const date = `${dateFormat}-01T00:00:00`
        const dateTimestamp = new Date(date).getTime()

        return !(dateTimestamp < todayTimestamp)
      }
    })
  },
  mounted () {
    this.isFetching = true
    this.renderPayload()
  }
}
</script>

<style lang="scss" scoped>
.period__disclaimer {
  background-color: $gray-2;
  font-size: 10px;
  margin-bottom: 24px;
  min-height: 48px;
  padding: 9px 16px;
  position: relative;

  @media (min-width: 992px) {
    margin-bottom: 0;
    margin-top: 24px;
  }

  &:before {
    content: '';
    display: none;
    border-bottom: 6px solid transparent;
    border-left: 6px solid transparent;
    border-right: 6px solid $gray-2;
    border-top: 6px solid transparent;
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);

    @media (min-width: 992px) {
      display: inline-block;
    }
  }
}

.textarea__restore {
  position: relative;

  .v-btn {
    margin: 0;
    position: absolute;
    right: 4px;
    top: 4px;
    z-index: 1;
  }
}

.input--padding-left {
  padding-left: 32px;
}
</style>

<style lang="scss">
.v-chip.theme--light.theme--dark {

  > .v-chip__content > .v-chip__close > .v-icon {
    color: #fff !important;
  }
}
</style>
